// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { base64ToJson, CodingContext, getRSAPublicKey } from '@imprivata-cloud/dataprivacy';
import type { AuthnRequest, EnrollPayload } from '@imprivata-cloud/authn';
import { ImprHeaders } from '@imprivata-cloud/common';
import { Endpoints } from './constants';
import type {
  SelfServiceLandingPageDataResponse,
  UserConsentCheckRequest,
  UserConsentCheckResponse,
  UserConsentRevokeRequest,
  UserConsentSubmitRequest,
  UserPasswordChangeRequest,
} from './types';
import { _getUrl, fetcher } from './utils';
import type { DomSerialized } from '@imprivata-cloud/components';

const getSelfServiceLandingPageData = async (): Promise<SelfServiceLandingPageDataResponse> => {
  return fetcher(Endpoints.GET_SELF_SERVICE_LANDING_PAGE_DATA, {
    method: 'POST',
  });
};

const submitConsent = async (data: UserConsentSubmitRequest): Promise<void> => {
  return fetcher(Endpoints.CONSENT_SUBMIT, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

const revokeConsent = async (data: UserConsentRevokeRequest): Promise<void> => {
  return fetcher(Endpoints.CONSENT_REVOKE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

const checkConsent = async (data: UserConsentCheckRequest): Promise<UserConsentCheckResponse> => {
  return fetcher(Endpoints.CONSENT_CHECK, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Get Legal Doc Content from json file
export async function getConsentLegalDocumentContent(url: string): Promise<DomSerialized> {
  return await fetcher(url, { method: 'GET', credentials: 'omit' });
}

const enroll = async (authnData: AuthnRequest): Promise<EnrollPayload> => {
  const { metadataUrl } = _getUrl({ metadataUrl: { sub: 'metadata.app' } });
  const publicDev = await getRSAPublicKey(`${metadataUrl}/data-privacy/public.json`);
  const codingContext = await CodingContext.createContextV2(publicDev.key, publicDev.version);
  const codingCtxHeader = codingContext.buildImprCodingCtxHeader();

  const factorDataObj = base64ToJson(authnData.factorData);
  const systemDataObj = base64ToJson(authnData.systemData);

  return fetcher(Endpoints.SELF_SERVICE_ENROLL, {
    method: 'POST',
    headers: {
      [ImprHeaders.ImprCodingCtx]: codingCtxHeader,
    },
    body: JSON.stringify({
      ...authnData,
      factorData: codingContext.encryptJson(factorDataObj),
      systemData: codingContext.encryptJson(systemDataObj),
    }),
  });
};

const changeImprIdpUserPassword = async (
  data: UserPasswordChangeRequest,
  codingContext?: CodingContext,
): Promise<void> => {
  return fetcher(Endpoints.SELF_SERVICE_CHANGE_PASSWORD, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: codingContext
      ? {
          'Impr-Coding-Ctx': codingContext.buildImprCodingCtxHeader(),
        }
      : {},
  });
};

export { getSelfServiceLandingPageData, submitConsent, checkConsent, enroll, changeImprIdpUserPassword, revokeConsent };
